import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { parcelAPI, tripAPI, bookingAPI } from '../../services/api';
import AppNavbar from '../../components/AppNavbar';
import SiteFooter from '../../components/SiteFooter';
import MobileBottomNav from '../../components/MobileBottomNav';
import MobileStepWizard from '../../components/MobileStepWizard';
import { arPrice } from '../../utils/arFormat';
import { WHATSAPP_NUMBER } from '../../config/whatsapp';
import { PARCEL_TYPES as ALL_PARCEL_TYPES } from '../../constants/parcelTypes';

// Mirrors backend/src/config/parcelFeatures.js — no frontend flag system
// exists either, so this stays a plain local constant edited alongside the
// backend one rather than a fetched/synced config.
const parcelFeatures = {
  requireTicketForPassengerParcel: true,
  hideInsuranceOnPassengerFlow: true,
};

// Only these Booking.status values represent a real, paid-or-confirmed seat —
// mirrors backend/src/services/parcelTicketService.js's ELIGIBLE_BOOKING_STATUSES.
const ELIGIBLE_BOOKING_STATUSES = ['payment_confirmed', 'ticket_issued'];

const PARCEL_TYPES = ALL_PARCEL_TYPES.filter((t) => t.passengerVisible);

const inputCls = 'w-full border-2 border-gray-200 hover:border-[#1E3A8A]/40 focus:border-[#1E3A8A] focus:ring-4 focus:ring-blue-100 rounded-xl px-4 py-2.5 text-sm transition-all outline-none';
const labelCls = 'block text-xs font-bold text-gray-600 mb-1.5';
const SUPPORT_WHATSAPP = WHATSAPP_NUMBER;

// Shared by both places a capacity problem can surface below: the trip
// search coming back empty because none of the scheduled trips can carry
// this weight, and the create-parcel request itself getting rejected for
// the same reason if a trip was picked before the weight was edited up.
const CAPACITY_CODES = new Set(['WEIGHT_EXCEEDED', 'COUNT_EXCEEDED', 'PARCELS_DISABLED']);

const CapacityWarning = ({ title = 'تعذّر إيجاد رحلة تتسع لهذا الوزن', message }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
    <p className="text-sm font-bold text-amber-800 mb-1">⚠️ {title}</p>
    <p className="text-xs text-amber-700 mb-3">{message}</p>
    <a
      href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent('مرحباً، لدي طرد وزنه أكبر من السعة المتاحة على الرحلات المجدولة، هل يمكن المساعدة؟')}`}
      target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold px-3.5 py-2 rounded-xl transition-colors"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12.017 2.003c-5.514 0-9.997 4.483-9.997 9.997 0 1.764.464 3.489 1.346 5.007L2.03 22l5.146-1.35a9.958 9.958 0 004.841 1.234h.004c5.514 0 9.997-4.483 9.997-9.997 0-2.67-1.04-5.182-2.929-7.071a9.935 9.935 0 00-7.072-2.929l.001.003z"/></svg>
      تواصل عبر واتساب لمساعدتك في إيجاد بديل
    </a>
  </div>
);

// Full (quadruple) name — at least 4 whitespace-separated parts, e.g.
// "أحمد محمد عبدالله علي" — required for both sender and receiver.
const isFullName = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).length >= 4;

const PassengerSendParcelPage = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('passenger_user') || '{}');
  const isLoggedIn = !!user?.id;

  const [step, setStep] = useState('form'); // form | payment | done
  const [wizardStep, setWizardStep] = useState(0);
  const [form, setForm] = useState({
    senderName: user.name || '', senderPhone: user.phone || '', senderNationalId: '',
    receiverName: '', receiverPhone: '',
    parcelType: 'general_goods', description: '', weightKg: '', sizeCategory: 'medium', quantity: 1,
    isInsured: false, declaredValue: '', isFragile: false, specialInstructions: '',
    fromCity: '', toCity: '', date: '',
    prohibitedItemsDeclared: false,
  });
  const set = (field) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [field]: v }));
  };

  const [cities, setCities] = useState([]);
  const [trips, setTrips] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedTripId, setSelectedTripId] = useState(null);
  const [departureOffices, setDepartureOffices] = useState([]);
  const [destinationOffices, setDestinationOffices] = useState([]);
  const [departureOfficeId, setDepartureOfficeId] = useState('');
  const [destinationOfficeId, setDestinationOfficeId] = useState('');
  const [quote, setQuote] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [capacityNote, setCapacityNote] = useState(null);
  const [capacityError, setCapacityError] = useState(null);

  // ── Ticket-linked shipping: the trip comes from the passenger's own
  //    booking, never from a free trip search. ──
  const [eligibleTickets, setEligibleTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(parcelFeatures.requireTicketForPassengerParcel);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [delegateToOther, setDelegateToOther] = useState(false);

  useEffect(() => {
    if (!parcelFeatures.requireTicketForPassengerParcel || !isLoggedIn) return;
    setLoadingTickets(true);
    bookingAPI.getMine()
      .then((res) => {
        const now = Date.now();
        const eligible = (res.data.data || []).filter((b) =>
          ELIGIBLE_BOOKING_STATUSES.includes(b.status) && b.trip && new Date(b.trip.departureTime).getTime() > now
        );
        setEligibleTickets(eligible);
        if (eligible.length === 1) setSelectedBookingId(eligible[0].id);
      })
      .catch(() => setEligibleTickets([]))
      .finally(() => setLoadingTickets(false));
  }, [isLoggedIn]);

  const selectedBooking = eligibleTickets.find((b) => b.id === selectedBookingId) || null;

  const [createdParcel, setCreatedParcel] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    tripAPI.getCities().then((res) => setCities(res.data.data || [])).catch(() => {});
  }, []);

  const selectedTrip = parcelFeatures.requireTicketForPassengerParcel
    ? (selectedBooking?.trip || null)
    : (trips.find((t) => t.id === selectedTripId) || null);

  const searchTrips = async () => {
    setSearching(true); setError(''); setTrips([]); setSelectedTripId(null); setCapacityNote(null);
    try {
      const res = await parcelAPI.getTrips({
        fromCity: form.fromCity || undefined,
        toCity: form.toCity || undefined,
        date: form.date || undefined,
        weightKg: form.weightKg || 0,
      });
      setTrips(res.data.data || []);
      setCapacityNote(res.data.capacityNote || null);
    } catch {
      setError('تعذّر البحث عن رحلات مناسبة');
    } finally { setSearching(false); }
  };

  // Load offices once a trip (and therefore a company) is chosen — departure
  // office must be in the trip's fromCity, destination in its toCity, and
  // both must be authorized to handle that company's trips.
  useEffect(() => {
    setDepartureOfficeId(''); setDestinationOfficeId('');
    if (!selectedTrip?.companyId) { setDepartureOffices([]); setDestinationOffices([]); return; }
    parcelAPI.getOffices(selectedTrip.companyId, selectedTrip.fromCity).then((res) => setDepartureOffices(res.data.data || [])).catch(() => setDepartureOffices([]));
    parcelAPI.getOffices(selectedTrip.companyId, selectedTrip.toCity).then((res) => setDestinationOffices(res.data.data || [])).catch(() => setDestinationOffices([]));
  }, [selectedTrip?.companyId, selectedTrip?.fromCity, selectedTrip?.toCity]);

  // Default (but editable) departure office = where the ticket was issued —
  // confirmed business rule. Only pre-fills once, when the office list loads
  // and nothing's been chosen yet; the passenger can still pick another.
  useEffect(() => {
    if (!selectedBooking?.officeId || departureOfficeId || departureOffices.length === 0) return;
    if (departureOffices.some((o) => o.id === selectedBooking.officeId)) {
      setDepartureOfficeId(selectedBooking.officeId);
    }
  }, [selectedBooking?.officeId, departureOffices, departureOfficeId]);

  // Live price quote
  const fetchQuote = useCallback(async () => {
    if (!form.weightKg) { setQuote(null); return; }
    try {
      const res = await parcelAPI.getQuote({
        weightKg: form.weightKg, quantity: form.quantity, isInsured: form.isInsured,
        declaredValue: form.declaredValue || 0, isFragile: form.isFragile,
        tripId: selectedTripId || undefined,
      });
      setQuote(res.data.data);
    } catch { setQuote(null); }
  }, [form.weightKg, form.quantity, form.isInsured, form.declaredValue, form.isFragile, selectedTripId]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);
  useEffect(() => { if (!isLoggedIn) navigate('/login?from=/parcels/new'); }, [isLoggedIn, navigate]);

  // "أنا أستلمه" (delegateToOther off, default): the ticket holder collects
  // their own parcel at the destination office, so the receiver IS the
  // sender — kept in sync here rather than only at submit time, so the
  // read-only receiver fields always reflect the current sender fields.
  useEffect(() => {
    if (delegateToOther) return;
    setForm((f) => (f.receiverName === f.senderName && f.receiverPhone === f.senderPhone)
      ? f : { ...f, receiverName: f.senderName, receiverPhone: f.senderPhone });
  }, [delegateToOther, form.senderName, form.senderPhone]);

  const toggleDelegate = () => {
    setDelegateToOther((prev) => {
      const next = !prev;
      if (next) setForm((f) => ({ ...f, receiverName: '', receiverPhone: '' }));
      return next;
    });
  };

  const ticketValid = !parcelFeatures.requireTicketForPassengerParcel || !!selectedBookingId;
  const peopleValid = isFullName(form.senderName) && isFullName(form.receiverName) && form.receiverPhone.trim();
  const parcelValid = !!form.weightKg;
  const detailsValid = peopleValid && parcelValid;
  const canSubmit = ticketValid && detailsValid && departureOfficeId && destinationOfficeId && form.prohibitedItemsDeclared;

  const handleCreate = async () => {
    if (parcelFeatures.requireTicketForPassengerParcel && !selectedBookingId) { setError('يرجى اختيار تذكرة سفر سارية لإرسال طرد'); return; }
    if (!isFullName(form.senderName)) { setError('يرجى إدخال الاسم الرباعي الكامل للمرسل'); return; }
    if (!isFullName(form.receiverName)) { setError('يرجى إدخال الاسم الرباعي الكامل للمستلم'); return; }
    if (!form.prohibitedItemsDeclared) { setError('يجب تأكيد أن الطرد لا يحتوي على مواد ممنوعة أو خطرة'); return; }
    if (!canSubmit) { setError('يرجى تعبئة جميع الحقول المطلوبة واختيار مكاتب الاستلام/التسليم'); return; }
    setCreating(true); setError(''); setCapacityError(null);
    try {
      const res = await parcelAPI.create({
        ...form,
        ...(parcelFeatures.requireTicketForPassengerParcel
          ? { bookingId: selectedBookingId }
          : { tripId: selectedTripId || undefined }),
        departureOfficeId, destinationOfficeId,
      });
      setCreatedParcel(res.data.data);
      if (selectedTrip?.id) {
        const infoRes = await tripAPI.getPaymentInfo(selectedTrip.id);
        setPaymentInfo(infoRes.data.data);
      }
      setStep('payment');
    } catch (err) {
      const data = err.response?.data;
      if (data?.code && CAPACITY_CODES.has(data.code)) {
        setCapacityError(data.message);
      } else {
        setError(data?.message || 'تعذّر إنشاء طلب الطرد');
      }
    } finally { setCreating(false); }
  };

  const handleUpload = async () => {
    if (!receipt) { setError('يرجى اختيار صورة الإيصال'); return; }
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('receipt', receipt);
      await parcelAPI.uploadPaymentProof(createdParcel.id, fd);
      setStep('done');
    } catch (err) {
      setError(err.response?.data?.message || 'تعذّر رفع الإيصال');
    } finally { setUploading(false); }
  };

  // ── Field groups, extracted once so desktop's single card and the mobile
  //    wizard's two steps render the exact same markup instead of duplicating it. ──
  const senderSection = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>الاسم الرباعي *</label>
        <input className={inputCls} value={form.senderName} onChange={set('senderName')} placeholder="مثال: أحمد محمد عبدالله علي" />
        {form.senderName.trim() && !isFullName(form.senderName) && (
          <p className="text-xs text-red-600 mt-1">يرجى إدخال الاسم الرباعي الكامل (٤ أسماء على الأقل)</p>
        )}
      </div>
      <div><label className={labelCls}>رقم الهاتف</label><input className={inputCls} value={form.senderPhone} onChange={set('senderPhone')} dir="ltr" /></div>
      <div><label className={labelCls}>الرقم الوطني (اختياري)</label><input className={inputCls} value={form.senderNationalId} onChange={set('senderNationalId')} /></div>
    </div>
  );

  const receiverSection = (
    <div>
      {parcelFeatures.requireTicketForPassengerParcel && (
        <label className="flex items-center gap-2 mb-3 cursor-pointer text-sm font-semibold text-gray-700">
          <input type="checkbox" checked={delegateToOther} onChange={toggleDelegate} />
          تسليم الطرد لشخص آخر
        </label>
      )}
      {parcelFeatures.requireTicketForPassengerParcel && !delegateToOther ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-600">
          ستستلم الطرد بنفسك من مكتب الوجهة ({form.senderName || '—'} / <span dir="ltr">{form.senderPhone || '—'}</span>)
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>الاسم الرباعي *</label>
            <input className={inputCls} value={form.receiverName} onChange={set('receiverName')} placeholder="مثال: أحمد محمد عبدالله علي" />
            {form.receiverName.trim() && !isFullName(form.receiverName) && (
              <p className="text-xs text-red-600 mt-1">يرجى إدخال الاسم الرباعي الكامل (٤ أسماء على الأقل)</p>
            )}
          </div>
          <div><label className={labelCls}>رقم الهاتف *</label><input className={inputCls} value={form.receiverPhone} onChange={set('receiverPhone')} dir="ltr" /></div>
        </div>
      )}
    </div>
  );

  const parcelSection = (
    <>
      <div className="flex gap-2 flex-wrap mb-4">
        {PARCEL_TYPES.map((t) => (
          <button key={t.key} type="button" onClick={() => setForm((f) => ({ ...f, parcelType: t.key }))}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
              form.parcelType === t.key ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]' : 'bg-gray-50 text-gray-700 border-gray-300 hover:border-[#1E3A8A]/50'
            }`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <div><label className={labelCls}>الوزن (كجم) *</label><input type="number" step="0.1" className={inputCls} value={form.weightKg} onChange={set('weightKg')} /></div>
        <div>
          <label className={labelCls}>الحجم</label>
          <select className={inputCls} value={form.sizeCategory} onChange={set('sizeCategory')}>
            <option value="small">صغير</option><option value="medium">متوسط</option><option value="large">كبير</option>
          </select>
        </div>
        <div><label className={labelCls}>الكمية</label><input type="number" min="1" className={inputCls} value={form.quantity} onChange={set('quantity')} /></div>
        <div className="flex items-end gap-2">
          <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600 mb-2.5">
            <input type="checkbox" checked={form.isFragile} onChange={set('isFragile')} /> قابل للكسر
          </label>
        </div>
      </div>
      <div className="mb-3"><label className={labelCls}>الوصف</label><input className={inputCls} value={form.description} onChange={set('description')} /></div>
      {!parcelFeatures.hideInsuranceOnPassengerFlow && (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
            <input type="checkbox" checked={form.isInsured} onChange={set('isInsured')} /> تأمين على الطرد
          </label>
          {form.isInsured && (
            <div><label className={labelCls}>القيمة المصرح بها</label><input type="number" className={inputCls} value={form.declaredValue} onChange={set('declaredValue')} /></div>
          )}
        </div>
      )}
      <div><label className={labelCls}>تعليمات خاصة</label><textarea rows={2} className={inputCls} value={form.specialInstructions} onChange={set('specialInstructions')} /></div>

      {quote && (
        <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs font-bold text-blue-800 mb-2">السعر التقديري</p>
          <div className="space-y-1 text-xs text-gray-700">
            {quote.breakdown.map((b, i) => (
              <div key={i} className="flex justify-between"><span>{b.label}</span><span className="tabular-nums">{arPrice(b.amount)} ج.س</span></div>
            ))}
          </div>
          <div className="flex justify-between mt-2 pt-2 border-t border-blue-200 font-black text-blue-900">
            <span>الإجمالي</span><span className="tabular-nums">{arPrice(quote.totalPrice)} ج.س</span>
          </div>
        </div>
      )}
    </>
  );

  // Ticket-linked shipping: the trip comes from the passenger's own booking,
  // never a free search. Auto-selected when there's exactly one eligible
  // ticket (handled in the fetch effect above); otherwise a radio selector or
  // a block-with-CTA when there are none.
  const ticketSection = (
    <>
      {loadingTickets && <p className="text-sm text-gray-500">...جارٍ تحميل تذاكرك</p>}

      {!loadingTickets && eligibleTickets.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-4 text-center">
          <p className="text-sm font-bold text-amber-800 mb-1">لا توجد لديك تذكرة سفر سارية</p>
          <p className="text-xs text-amber-700 mb-3">إرسال الطرود متاح حالياً فقط للركاب الحاملين تذكرة سفر على نفس الرحلة.</p>
          <button type="button" onClick={() => navigate('/search')}
            className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white text-xs font-bold px-4 py-2 rounded-xl">
            احجز تذكرة سفر
          </button>
        </div>
      )}

      {!loadingTickets && eligibleTickets.length > 1 && (
        <div className="space-y-2 mb-3">
          {eligibleTickets.map((b) => (
            <label key={b.id} className={`flex items-center gap-3 border rounded-xl px-4 py-3 cursor-pointer transition-all ${
              selectedBookingId === b.id ? 'border-[#1E3A8A] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}>
              <input type="radio" name="ticket" checked={selectedBookingId === b.id} onChange={() => setSelectedBookingId(b.id)} />
              <div className="text-sm">
                <span className="font-bold text-gray-800">{b.trip?.fromCity} ← {b.trip?.toCity}</span>
                <span className="text-gray-500 mx-2 text-xs">{new Date(b.trip?.departureTime).toLocaleString('ar-SA', { weekday: 'short', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</span>
                <span className="text-blue-700 text-xs font-semibold">{b.trip?.company?.name}</span>
                <span className="text-gray-400 text-xs mx-2 font-mono">#{b.bookingNumber}</span>
              </div>
            </label>
          ))}
        </div>
      )}

      {selectedBooking && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
          <p className="text-xs font-bold text-blue-800 mb-2">تفاصيل الرحلة</p>
          <div className="grid grid-cols-2 gap-y-1.5 text-sm text-gray-700">
            <span className="text-gray-500 text-xs">رقم التذكرة</span><span className="font-mono font-bold">{selectedBooking.bookingNumber}</span>
            <span className="text-gray-500 text-xs">شركة النقل</span><span>{selectedBooking.trip?.company?.name || '—'}</span>
            <span className="text-gray-500 text-xs">من</span><span>{selectedBooking.trip?.fromCity}</span>
            <span className="text-gray-500 text-xs">إلى</span><span>{selectedBooking.trip?.toCity}</span>
            <span className="text-gray-500 text-xs">تاريخ السفر ووقت المغادرة</span>
            <span>{selectedBooking.trip?.departureTime ? new Date(selectedBooking.trip.departureTime).toLocaleString('ar-SA', { weekday: 'long', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : '—'}</span>
          </div>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-3">
            يرجى تسليم الطرد إلى مكتب شركة النقل قبل موعد المغادرة بـ 30 دقيقة على الأقل.
          </p>
        </div>
      )}
    </>
  );

  const tripSection = (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <div>
          <label className={labelCls}>من</label>
          <input list="parcel-cities" className={inputCls} value={form.fromCity} onChange={set('fromCity')} />
        </div>
        <div>
          <label className={labelCls}>إلى</label>
          <input list="parcel-cities" className={inputCls} value={form.toCity} onChange={set('toCity')} />
        </div>
        <div><label className={labelCls}>التاريخ (اختياري)</label><input type="date" className={inputCls} value={form.date} onChange={set('date')} /></div>
      </div>
      <datalist id="parcel-cities">{cities.map((c) => <option key={c} value={c} />)}</datalist>
      <button type="button" onClick={searchTrips} disabled={searching}
        className="bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-60 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all">
        {searching ? '...جارٍ البحث' : 'ابحث عن رحلات'}
      </button>

      {trips.length > 0 && (
        <div className="mt-4 space-y-2">
          {trips.map((t) => (
            <label key={t.id} className={`flex items-center justify-between gap-3 border rounded-xl px-4 py-3 cursor-pointer transition-all ${
              selectedTripId === t.id ? 'border-[#1E3A8A] bg-blue-50' : 'border-gray-200 hover:border-gray-300'
            }`}>
              <div className="flex items-center gap-3">
                <input type="radio" name="trip" checked={selectedTripId === t.id} onChange={() => setSelectedTripId(t.id)} />
                <div className="text-sm">
                  <span className="font-bold text-gray-800">{t.fromCity} ← {t.toCity}</span>
                  <span className="text-gray-500 mx-2 text-xs">{new Date(t.departureTime).toLocaleString('ar-SA', { weekday: 'short', hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}</span>
                  <span className="text-blue-700 text-xs font-semibold">{t.company?.name}</span>
                </div>
              </div>
              {t.cargoCapacity?.maxWeightKg != null && (
                <span className="text-emerald-700 bg-emerald-50 border border-emerald-100 text-xs font-semibold px-2 py-1 rounded-lg flex-shrink-0">
                  📦 متاح {t.cargoCapacity.availableWeightKg} كجم
                </span>
              )}
            </label>
          ))}
        </div>
      )}
      {trips.length === 0 && !searching && capacityNote && (
        <div className="mt-4"><CapacityWarning message={capacityNote.message} /></div>
      )}
      {selectedTrip === null && trips.length === 0 && !searching && !capacityNote && (
        <p className="text-xs text-gray-500 mt-3">لم تختر رحلة بعد؟ يمكنك المتابعة بدون رحلة وسيتم تعيينها لاحقاً بعد تأكيد الدفع.</p>
      )}
    </>
  );

  const officesSection = (
    !selectedTrip ? (
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">اختر رحلة أولاً لعرض مكاتب الشركة في مدينتي الانطلاق والوصول</p>
    ) : (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>مكتب المغادرة ({selectedTrip.fromCity})</label>
          <select className={inputCls} value={departureOfficeId} onChange={(e) => setDepartureOfficeId(e.target.value)}>
            <option value="">اختر المكتب</option>
            {departureOffices.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.city}</option>)}
          </select>
          {departureOffices.length === 0 && (
            <p className="text-xs text-red-600 mt-1.5">لا يوجد مكتب تابع لهذه الشركة في {selectedTrip.fromCity}</p>
          )}
        </div>
        <div>
          <label className={labelCls}>مكتب الوجهة ({selectedTrip.toCity})</label>
          <select className={inputCls} value={destinationOfficeId} onChange={(e) => setDestinationOfficeId(e.target.value)}>
            <option value="">اختر المكتب</option>
            {destinationOffices.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.city}</option>)}
          </select>
          {destinationOffices.length === 0 && (
            <p className="text-xs text-red-600 mt-1.5">لا يوجد مكتب تابع لهذه الشركة في {selectedTrip.toCity}</p>
          )}
        </div>
      </div>
    )
  );

  const declarationSection = (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input type="checkbox" className="mt-0.5 w-4 h-4" checked={form.prohibitedItemsDeclared} onChange={set('prohibitedItemsDeclared')} />
      <span className="text-sm text-gray-700">
        أؤكد أن هذا الطرد <strong>لا يحتوي</strong> على مواد ممنوعة أو خطرة أو غير قانونية، وأتفهم أن المكتب يحتفظ بحق فحص الطرد ورفض استلامه.
      </span>
    </label>
  );

  // ── Mobile wizard: the full journey in 3 steps — sender/receiver, then
  //    parcel details, then everything remaining (route + offices +
  //    declaration + submit). ──
  const wizardSteps = [
    {
      title: 'بيانات المرسل والمستلم',
      subtitle: 'من يرسل، ولمن',
      isValid: peopleValid,
      render: () => (
        <div className="space-y-6">
          <div>
            <h3 className="font-bold text-gray-800 mb-3 text-sm">بيانات المرسل</h3>
            {senderSection}
          </div>
          <div className="pt-4 border-t border-gray-100">
            <h3 className="font-bold text-gray-800 mb-3 text-sm">بيانات المستلم</h3>
            {receiverSection}
          </div>
        </div>
      ),
    },
    {
      title: 'بيانات الطرد',
      subtitle: 'ماذا ترسل؟',
      isValid: parcelValid,
      render: () => parcelSection,
    },
    {
      title: 'الرحلة ومكاتب التسليم',
      subtitle: 'اختر رحلة ومكتب الاستلام والتسليم، وأكّد الإقرار',
      isValid: canSubmit,
      render: () => (
        <div className="space-y-6">
          <div>
            <h3 className="font-bold text-gray-800 mb-3 text-sm">{parcelFeatures.requireTicketForPassengerParcel ? 'تذكرة السفر' : 'البحث عن رحلة'}</h3>
            {parcelFeatures.requireTicketForPassengerParcel ? ticketSection : tripSection}
          </div>
          <div className="pt-4 border-t border-gray-100">
            <h3 className="font-bold text-gray-800 mb-3 text-sm">مكاتب الاستلام والتسليم</h3>
            {officesSection}
          </div>
          <div className="pt-4 border-t border-gray-100">
            {declarationSection}
          </div>
        </div>
      ),
    },
  ];

  if (!isLoggedIn) return null;

  return (
    <div dir="rtl" className="min-h-screen bg-[#F5F9FF]">
      <AppNavbar />
      <div className="max-w-3xl mx-auto px-4 py-6 hidden md:block pb-16">
        <h1 className="text-xl font-black text-[#1E3A8A] mb-1">📦 إرسال طرد</h1>
        <p className="text-sm text-gray-600 mb-6">أرسل طرداً عبر رحلاتنا اليومية — تتبّع طردك من لحظة الإرسال حتى الاستلام</p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>
        )}
        {capacityError && <CapacityWarning title="تعذّر إتمام الطلب على هذه الرحلة" message={capacityError} />}

        {step === 'form' && (
          <div>
            {/* One continuous card holding every section — matches مشاوير's
                single-card pattern instead of several separately-boxed
                sections. Same fields/logic throughout, purely a layout merge. */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-6">
              <div>
                <h2 className="font-bold text-gray-800 mb-3">بيانات المرسل</h2>
                {senderSection}
              </div>
              <div className="border-t border-gray-100 pt-6">
                <h2 className="font-bold text-gray-800 mb-3">بيانات المستلم</h2>
                {receiverSection}
              </div>
              <div className="border-t border-gray-100 pt-6">
                <h2 className="font-bold text-gray-800 mb-3">بيانات الطرد</h2>
                {parcelSection}
              </div>
              <div className="border-t border-gray-100 pt-6">
                <h2 className="font-bold text-gray-800 mb-3">{parcelFeatures.requireTicketForPassengerParcel ? 'تذكرة السفر' : 'البحث عن رحلة'}</h2>
                {parcelFeatures.requireTicketForPassengerParcel ? ticketSection : tripSection}
              </div>
              <div className="border-t border-gray-100 pt-6">
                <h2 className="font-bold text-gray-800 mb-3">مكاتب الاستلام والتسليم</h2>
                {officesSection}
              </div>
              <div className="border-t border-gray-100 pt-6">
                {declarationSection}
              </div>
            </div>

            <button type="button" onClick={handleCreate} disabled={creating || !canSubmit}
              className="w-full mt-5 bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-black py-3.5 rounded-xl transition-all">
              {creating ? '...جارٍ الإنشاء' : 'متابعة إلى الدفع'}
            </button>
          </div>
        )}

        {step === 'payment' && createdParcel && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold text-gray-800 mb-1">الدفع</h2>
            <p className="text-xs text-gray-500 mb-4">رقم التتبع: <span className="font-mono font-bold text-blue-700">{createdParcel.trackingNumber}</span></p>

            {paymentInfo?.bankAccounts?.length > 0 ? (
              <div className="space-y-2 mb-4">
                {paymentInfo.bankAccounts.map((acc, i) => (
                  <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                    <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">{acc.bankName}</span>
                    <div className="mt-2 text-sm"><span className="text-gray-500 text-xs">رقم الحساب: </span><span dir="ltr" className="font-mono font-bold">{acc.accountNumber}</span></div>
                    <div className="text-sm"><span className="text-gray-500 text-xs">اسم الحساب: </span>{acc.accountHolder}</div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 mb-4">سيتم تزويدك بتفاصيل الدفع بعد تعيين رحلة لطردك، أو تواصل مع المكتب مباشرة.</p>
            )}

            <label className={labelCls}>إيصال التحويل</label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setReceipt(e.target.files[0])} className="text-sm mb-4" />

            <button onClick={handleUpload} disabled={uploading || !receipt}
              className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-black py-3.5 rounded-xl transition-all">
              {uploading ? '...جارٍ الرفع' : 'رفع الإيصال'}
            </button>
          </div>
        )}

        {step === 'done' && createdParcel && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <div className="text-5xl mb-3">✅</div>
            <h2 className="font-black text-lg text-gray-900 mb-2">تم إرسال طلب الطرد بنجاح</h2>
            <p className="text-sm text-gray-600 mb-4">رقم التتبع الخاص بك</p>
            <p className="font-mono font-black text-2xl text-blue-700 mb-6">{createdParcel.trackingNumber}</p>
            <p className="text-xs text-gray-500 mb-6">سيتم مراجعة الدفع من قبل الشركة، ثم يمكنك تسليم الطرد في مكتب المغادرة. يمكنك متابعة الحالة من صفحة "طرودي".</p>
            <button onClick={() => navigate('/parcels')} className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white font-bold px-6 py-3 rounded-xl">
              عرض طرودي
            </button>
          </div>
        )}
      </div>

      {/* Mobile: same 2-step wizard pattern as نقل عام. The payment/done steps
          are short, single-purpose screens already, so they're shown as-is
          (no scroll-heavy content to split) once the form step is complete. */}
      {step === 'form' && (
        <MobileStepWizard
          steps={wizardSteps}
          step={wizardStep}
          setStep={setWizardStep}
          onSubmit={handleCreate}
          submitting={creating}
          submitLabel="متابعة إلى الدفع"
          error={error || capacityError}
          onExit={() => navigate(-1)}
        />
      )}
      {step !== 'form' && (
        <div className="md:hidden px-4 py-6 pb-24">
          {step === 'payment' && createdParcel && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" dir="rtl">
              <h2 className="font-bold text-gray-800 mb-1">الدفع</h2>
              <p className="text-xs text-gray-500 mb-4">رقم التتبع: <span className="font-mono font-bold text-blue-700">{createdParcel.trackingNumber}</span></p>

              {paymentInfo?.bankAccounts?.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {paymentInfo.bankAccounts.map((acc, i) => (
                    <div key={i} className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">{acc.bankName}</span>
                      <div className="mt-2 text-sm"><span className="text-gray-500 text-xs">رقم الحساب: </span><span dir="ltr" className="font-mono font-bold">{acc.accountNumber}</span></div>
                      <div className="text-sm"><span className="text-gray-500 text-xs">اسم الحساب: </span>{acc.accountHolder}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500 mb-4">سيتم تزويدك بتفاصيل الدفع بعد تعيين رحلة لطردك، أو تواصل مع المكتب مباشرة.</p>
              )}

              <label className={labelCls}>إيصال التحويل</label>
              <input type="file" accept="image/*,application/pdf" onChange={(e) => setReceipt(e.target.files[0])} className="text-sm mb-4" />

              <button onClick={handleUpload} disabled={uploading || !receipt}
                className="w-full bg-[#1E40AF] hover:bg-[#1D4ED8] disabled:opacity-50 text-white font-black py-3.5 rounded-xl transition-all">
                {uploading ? '...جارٍ الرفع' : 'رفع الإيصال'}
              </button>
            </div>
          )}
          {step === 'done' && createdParcel && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center" dir="rtl">
              <div className="text-5xl mb-3">✅</div>
              <h2 className="font-black text-lg text-gray-900 mb-2">تم إرسال طلب الطرد بنجاح</h2>
              <p className="text-sm text-gray-600 mb-4">رقم التتبع الخاص بك</p>
              <p className="font-mono font-black text-2xl text-blue-700 mb-6">{createdParcel.trackingNumber}</p>
              <p className="text-xs text-gray-500 mb-6">سيتم مراجعة الدفع من قبل الشركة، ثم يمكنك تسليم الطرد في مكتب المغادرة. يمكنك متابعة الحالة من صفحة "طرودي".</p>
              <button onClick={() => navigate('/parcels')} className="bg-[#1E40AF] hover:bg-[#1D4ED8] text-white font-bold px-6 py-3 rounded-xl">
                عرض طرودي
              </button>
            </div>
          )}
        </div>
      )}

      <div className="hidden md:block"><SiteFooter navigate={navigate} /></div>
      <MobileBottomNav />
    </div>
  );
};

export default PassengerSendParcelPage;
